import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import Card from '../common/Card';
import InlineLoader from '../common/InlineLoader';
import Button from '../common/Button';
import { formatUSDT } from '../../utils/format';

const PAGE = 20;

export default function TeamTree() {
  const { account } = useWallet();
  const { main, views } = useContracts();
  const [loading, setLoading] = useState(true);
  const [lvlData, setLvlData] = useState(null);
  const [selectedLvl, setSelectedLvl] = useState(0);
  const [users, setUsers] = useState([]);
  const [pg, setPg] = useState(0);
  const [total, setTotal] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!account) { setLoading(false); return; }
    const load = async () => {
      try {
        const uid = await main.addrToId(account);
        if (uid.eq(0)) { if (mountedRef.current) setLoading(false); return; }
        const [counts, vols] = await Promise.all([
          main.getUserLvlCounts(uid),
          main.getUserLvlVols(uid)
        ]);
        if (!mountedRef.current) return;
        setLvlData({ uid, counts, vols });
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main]);

  useEffect(() => {
    mountedRef.current = true;
    if (!lvlData) return;
    const load = async () => {
      try {
        setLoadingUsers(true);
        const res = await views.getLvlUsers(lvlData.uid, selectedLvl, pg, PAGE);
        if (!mountedRef.current) return;
        setUsers(res[0].map((x) => x.toString()));
        setTotal(Number(res[1].toString()));
        setLoadingUsers(false);
      } catch (_) {
        if (mountedRef.current) setLoadingUsers(false);
      }
    };
    load();
    return () => { mountedRef.current = false; };
  }, [lvlData, views, selectedLvl, pg]);

  if (loading) return <Card title="Team Tree"><InlineLoader /></Card>;
  if (!account) return <Card title="Team Tree"><div className="empty-state">Connect wallet</div></Card>;
  if (!lvlData) return <Card title="Team Tree"><div className="empty-state">Not registered</div></Card>;

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <>
      <Card title="Team Overview (20 Levels)" glow>
        <div className="table-wrap table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Level</th>
                <th className="text-right">Members</th>
                <th className="text-right">Volume</th>
                <th className="text-center">View</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 20 }).map((_, i) => (
                <tr key={i}>
                  <td><span className="level-num">L{i + 1}</span></td>
                  <td className="text-right">{lvlData.counts[i].toString()}</td>
                  <td className="text-right text-primary">${formatUSDT(lvlData.vols[i], 0)}</td>
                  <td className="text-center">
                    <Button
                      variant={selectedLvl === i ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => { setSelectedLvl(i); setPg(0); }}
                    >
                      {selectedLvl === i ? 'VIEWING' : 'VIEW'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4">
        <Card title={`Level ${selectedLvl + 1} Users (${total})`}>
          {loadingUsers ? <InlineLoader /> : (
            <>
              {users.length === 0 ? (
                <div className="empty-state">No users at this level yet</div>
              ) : (
                <div className="table-wrap table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>User ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, idx) => (
                        <tr key={idx}>
                          <td>{pg * PAGE + idx + 1}</td>
                          <td className="text-primary">#{u}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPages > 1 && (
                <div className="pagination">
                  <button className="page-btn" disabled={pg === 0} onClick={() => setPg((p) => Math.max(0, p - 1))}>← PREV</button>
                  <span className="page-info">Page {pg + 1} / {totalPages}</span>
                  <button className="page-btn" disabled={pg >= totalPages - 1} onClick={() => setPg((p) => p + 1)}>NEXT →</button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  );
}
